/**
 * Garnkit — core configurator logic.
 *
 * Pure ES module: no DOM, no fetch, no globals, no mutation of its arguments.
 * Everything here is a function of (state, recipe, catalog). Derived values —
 * skein counts, line prices, totals, add-to-cart availability, per-swatch flags —
 * are computed on every call and NEVER written back onto state.
 *
 * Shapes
 *   state   = { size: null|'<sizeCode>', colors: { <slotKey>: <variantId>|null }, expanded: <slotKey>|null }
 *   recipe  = the $app:kit_recipe metafield JSON (sizes[{code,chest}], slots[{key,label,handle,material,skeins{}}], pattern, fromPrice)
 *   catalog = { <slotKey>: { title, price, variants: [ /products/{handle}.js variant objects ] } }
 *
 * Money is always in øre (integers). Danish copy is verbatim from docs/BUILD-CONTEXT.md.
 */

/** Non-breaking space — keeps "65,00 kr" from wrapping between amount and unit. */
const NBSP = '\u00A0';

/** Leading colour number + the rest of the name, e.g. "105 Slate Green" / "977 Marzipan (melange)". */
const CODE_RE = /^\s*(\d+)(?:\s+([\s\S]*))?$/;

/* ------------------------------------------------------------------ *
 * Internal helpers
 * ------------------------------------------------------------------ */

/** The yarn slots of a recipe, in the order the customer sees them. */
function yarnSlots(recipe) {
  return Array.isArray(recipe && recipe.slots) ? recipe.slots : [];
}

function findSlot(recipe, slotKey) {
  return yarnSlots(recipe).find((s) => s && s.key === slotKey) || null;
}

function catalogEntry(catalog, slotKey) {
  return (catalog && catalog[slotKey]) || null;
}

function entryVariants(entry) {
  return entry && Array.isArray(entry.variants) ? entry.variants : [];
}

/** Unit price in øre for a quality: the product price, falling back to its first variant. */
function unitPriceOf(entry) {
  if (!entry) return 0;
  if (Number.isFinite(entry.price)) return entry.price;
  const first = entryVariants(entry)[0];
  return first && Number.isFinite(first.price) ? first.price : 0;
}

/** Splits a variant title into its leading colour code and the remaining name. */
function splitTitle(rawTitle) {
  const title = String(rawTitle == null ? '' : rawTitle).trim();
  const m = CODE_RE.exec(title);
  if (!m) return { code: null, name: title };
  return { code: m[1], name: (m[2] || '').trim() };
}

/** "123 Lichen" — what the swatch label, price panel and notices print. */
function displayColour(code, name) {
  if (code && name) return `${code} ${name}`;
  return code || name || '';
}

/**
 * Inventory count for a variant, or null when it cannot be substantiated.
 *
 * `/products/{handle}.js` does NOT expose inventory_quantity — it is merged in from
 * Liquid at render time. When it is absent, or Shopify is not tracking inventory for
 * the variant, we degrade to `available` only and never claim a count.
 */
function readStock(variant) {
  if (!variant) return null;
  if ('inventory_management' in variant) {
    const im = variant.inventory_management;
    if (im === null || im === undefined || im === '' || im === false) return null;
  }
  const q = variant.inventory_quantity;
  return Number.isInteger(q) ? q : null;
}

function variantImage(variant) {
  const img = variant && variant.featured_image;
  return img && typeof img.src === 'string' && img.src ? img.src : null;
}

/** The raw catalog variant a slot currently points at, or null. */
function rawChosen(state, catalog, slotKey) {
  const chosen = (state && state.colors && state.colors[slotKey]) ?? null;
  if (chosen === null || chosen === undefined) return null;
  const found = entryVariants(catalogEntry(catalog, slotKey)).find((v) => v && v.id === chosen);
  return found || null;
}

/* ------------------------------------------------------------------ *
 * Public API
 * ------------------------------------------------------------------ */

/**
 * Formats an amount in øre as Danish kroner: `formatDKK(6500) === "65,00 kr"`.
 * Comma decimal, dot thousands separator, non-breaking space before `kr`.
 */
export function formatDKK(oere) {
  const n = Math.round(Number(oere) || 0);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const kroner = Math.floor(abs / 100);
  const decimals = String(abs % 100).padStart(2, '0');
  const grouped = String(kroner).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${sign}${grouped},${decimals}${NBSP}kr`;
}

/**
 * How many skeins (nøgler) of `slotKey` the given size needs.
 * Quantities are keyed by size CODE, never by index. Returns null when no size is
 * chosen, when the slot is unknown, or when the recipe has no entry for that size.
 */
export function requiredSkeins(recipe, slotKey, sizeCode) {
  if (sizeCode === null || sizeCode === undefined) return null;
  if (typeof sizeCode !== 'string') return null;
  const slot = findSlot(recipe, slotKey);
  if (!slot || !slot.skeins) return null;
  const n = slot.skeins[sizeCode];
  return Number.isFinite(n) ? n : null;
}

/**
 * Annotates raw variants for rendering as swatches.
 *
 * status:
 *   'sold_out' — available === false, whatever the count says
 *   'low'      — a known count below `required`
 *   'ok'       — everything else, including an unknown count (degrade path:
 *                never claim a shortage we cannot substantiate)
 */
export function annotateVariants(variants, required, lowStockThreshold) {
  const list = Array.isArray(variants) ? variants : [];
  const need = Number.isFinite(required) ? required : null;
  // The threshold is a merchant EARLY-WARNING floor (Settings › "lagergrænse").
  // It drives `earlyWarning` only — a separate, purely informational signal.
  // It never touches `status`, which stays the hard oversell/checkout gate
  // that slotView/addToCartState rely on: a colour with enough stock for
  // THIS kit is 'ok' no matter how low that is against the merchant's own
  // restock threshold. Conflating the two would let a restock reminder
  // silently block a genuinely fulfillable add-to-cart.
  const threshold = Number.isFinite(lowStockThreshold) && lowStockThreshold > 0 ? lowStockThreshold : null;

  return list.map((v) => {
    const { code, name } = splitTitle(v && (v.title ?? v.option1 ?? v.public_title));
    const available = !(v && v.available === false);
    const stock = readStock(v);

    let status = 'ok';
    if (!available) status = 'sold_out';
    else if (need !== null && stock !== null && stock < need) status = 'low';

    const earlyWarning = status === 'ok' && stock !== null && threshold !== null && stock < threshold;

    return {
      id: v ? v.id : null,
      code,
      name,
      image: variantImage(v),
      price: v && Number.isFinite(v.price) ? v.price : 0,
      available,
      stock,
      status,
      earlyWarning
    };
  });
}

/**
 * Everything one yarn slot needs in order to render.
 *
 * status:
 *   'locked'         — no size chosen yet; the slot is dimmed and inert
 *   'missing_colour' — size chosen, no colour yet
 *   'insufficient'   — the chosen colour is sold out or short of stock (never cleared)
 *   'ready'          — chosen and buyable
 *
 * Returns null for an unknown slot key.
 */
export function slotView(state, recipe, catalog, slotKey, lowStockThreshold) {
  const slot = findSlot(recipe, slotKey);
  if (!slot) return null;

  const entry = catalogEntry(catalog, slotKey);
  const size = (state && state.size) ?? null;
  const required = requiredSkeins(recipe, slotKey, size);
  const variants = annotateVariants(entryVariants(entry), required, lowStockThreshold);

  const chosenId = (state && state.colors && state.colors[slotKey]) ?? null;
  const chosenVariant = chosenId === null ? null : variants.find((v) => v.id === chosenId) || null;

  let status;
  if (size === null) status = 'locked';
  else if (!chosenVariant) status = 'missing_colour';
  else if (chosenVariant.status !== 'ok') status = 'insufficient';
  else status = 'ready';

  return {
    key: slot.key,
    label: slot.label,
    material: slot.material ?? null,
    unitPrice: unitPriceOf(entry),
    required,
    chosen: chosenId,
    chosenVariant,
    status,
    variants,
    expanded: (state && state.expanded) === slotKey
  };
}

/**
 * The price panel: one line per yarn quality plus the included pattern line.
 * `colourName` is the printed colour label ("123 Lichen"), null while unchosen.
 * opts.patternOptedOut — when true, the customer has opted out of the recipe;
 * the pattern line is omitted entirely from the output.
 */
export function lines(state, recipe, catalog, opts) {
  const out = yarnSlots(recipe).map((slot) => {
    const view = slotView(state, recipe, catalog, slot.key);
    const qty = view.required;
    const colour = view.chosenVariant;
    const unitPrice = (colour && Number.isFinite(colour.price)) ? colour.price : view.unitPrice;
    return {
      key: view.key,
      label: view.label,
      colourName: colour ? displayColour(colour.code, colour.name) : null,
      qty,
      unitPrice,
      lineTotal: (qty ?? 0) * unitPrice
    };
  });

  const pattern = (recipe && recipe.pattern) || null;
  if (pattern && !(opts && opts.patternOptedOut)) {
    const patternPrice =
      pattern.price_override != null && Number.isFinite(pattern.price_override) && pattern.price_override >= 0
        ? pattern.price_override
        : (Number.isFinite(pattern.livePrice) && pattern.livePrice >= 0 ? pattern.livePrice : 0);
    out.push({
      key: 'pattern',
      label: pattern.label || 'Opskrift (PDF)',
      colourName: null,
      qty: 1,
      unitPrice: patternPrice,
      lineTotal: patternPrice
    });
  }

  return out;
}

/** Kit total in øre — the sum of every line, pattern included. */
export function total(state, recipe, catalog, opts) {
  return lines(state, recipe, catalog, opts).reduce((sum, line) => sum + line.lineTotal, 0);
}

/**
 * Whether the kit can be added to the cart, and — when it cannot — why, in Danish.
 * Precedence: no size, then a missing colour (naming the first offending quality),
 * then insufficient stock. The reason is derived from state, never hardcoded upstream.
 */
export function addToCartState(state, recipe, catalog) {
  const size = (state && state.size) ?? null;
  if (size === null) {
    return {
      enabled: false,
      reasonKind: 'no_size',
      reason: 'Vælg en størrelse for at fortsætte.'
    };
  }

  const views = yarnSlots(recipe).map((slot) => slotView(state, recipe, catalog, slot.key));

  const missing = views.find((v) => v.status === 'missing_colour');
  if (missing) {
    return {
      enabled: false,
      reasonKind: 'missing_colour',
      reason: `Vælg farve for hver garnkvalitet — ${missing.label} mangler.`
    };
  }

  const short = views.find((v) => v.status === 'insufficient');
  if (short) {
    return {
      enabled: false,
      reasonKind: 'insufficient',
      reason: `En valgt farve rækker ikke til str. ${size}. Skift farve eller størrelse.`
    };
  }

  return { enabled: true, reasonKind: null, reason: null };
}

/** True when a whole quality has no buyable colour left (or no colours at all). */
export function qualityUnavailable(recipe, catalog, slotKey) {
  const variants = entryVariants(catalogEntry(catalog, slotKey));
  return !variants.some((v) => v && v.available !== false);
}

/**
 * The "Du skiftede til str. XL …" explanation.
 *
 * Colour choices are NEVER silently cleared — when a size change leaves a preserved
 * colour short of stock, we say so instead. Returns the first offending slot's sentence
 * in recipe order, or null when nothing needs explaining. Stays silent when the count is
 * unknown, and when the colour is sold out (that is not a size problem).
 */
export function sizeChangeNotice(prevSize, nextSize, state, recipe, catalog) {
  if (prevSize === null || prevSize === undefined) return null;
  if (nextSize === null || nextSize === undefined) return null;
  if (prevSize === nextSize) return null;

  for (const slot of yarnSlots(recipe)) {
    const raw = rawChosen(state, catalog, slot.key);
    if (!raw) continue;
    if (raw.available === false) continue;

    const stock = readStock(raw);
    if (stock === null) continue;

    const need = requiredSkeins(recipe, slot.key, nextSize);
    if (need === null || stock >= need) continue;

    const { code, name } = splitTitle(raw.title ?? raw.option1 ?? raw.public_title);
    const colour = displayColour(code, name);
    return (
      `Du skiftede til str. ${nextSize}. ${colour} er stadig valgt, men rækker ikke længere ` +
      `(${stock} af ${need}). Valget er bevaret — skift farve eller størrelse for at fortsætte.`
    );
  }

  return null;
}

/**
 * The /cart/add.js body: one real line item per yarn quality (so stock stays shared with
 * standalone yarn sales and Shopify decrements every SKU natively), all sharing one
 * `_kit_id` so Liquid can group them into a single cart card.
 *
 * Returns [] unless the kit is structurally complete (size + a resolvable colour per slot).
 * Stock is deliberately NOT re-checked here — /cart/add.js is the final authority on
 * oversell, and addToCartState already gates the button.
 *
 * The pattern is emitted as its own line only when the recipe gives it a variant id;
 * the seed recipe ships the PDF as an included, priceless part of the kit.
 */
export function cartPayload(state, recipe, catalog, meta, opts) {
  const size = (state && state.size) ?? null;
  if (size === null) return [];

  const { kitId = '', kitHandle = '', kitTitle = '' } = meta || {};
  const slots = yarnSlots(recipe);
  const items = [];

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const raw = rawChosen(state, catalog, slot.key);
    const qty = requiredSkeins(recipe, slot.key, size);
    if (!raw || qty === null) return [];

    items.push({
      id: raw.id,
      quantity: qty,
      properties: {
        _kit_id: String(kitId),
        _kit_ref: String(kitHandle),
        _kit_slot: String(i),
        _kit_size: String(size),
        _kit_skeins: String(qty),
        Kit: `${kitTitle} — str. ${size}`
      }
    });
  }

  if (items.length === 0) return [];

  const pattern = (recipe && recipe.pattern) || null;
  const patternOptedOut = opts && opts.patternOptedOut;
  if (!patternOptedOut && pattern && (Number.isFinite(pattern.variantId) || typeof pattern.variantId === 'string')) {
    items.push({
      id: pattern.variantId,
      quantity: 1,
      properties: {
        _kit_id: String(kitId),
        _kit_ref: String(kitHandle),
        _kit_slot: 'pattern',
        _kit_size: String(size),
        _kit_skeins: '1',
        Kit: `${kitTitle} — str. ${size}`
      }
    });
  }

  return items;
}
